import { useEffect, useState } from "react";
import { Save, Store, Truck, BadgeEuro } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";
import ReviewsAdminPanel from "./ReviewsAdminPanel";
import CleanupPanel from "./CleanupPanel";
import ReferralLeaderboard from "./ReferralLeaderboard";

const STATUS_COLORS = {
  PAID: "bg-green-100 text-green-700", PENDING: "bg-amber-100 text-amber-700", AWAITING_TRANSFER: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-amber-100 text-amber-700", FAILED: "bg-red-100 text-red-700",
  SHIPPED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-stone-200 text-stone-600", ERROR: "bg-red-100 text-red-700",
};

export default function ShopPanel() {
  const [form, setForm] = useState({ shipping_cost: 7, free_shipping_threshold: 79, enabled: true, payment_ready: false });
  const [orders, setOrders] = useState([]);

  const load = () => {
    api.get("/settings/shop").then(({ data }) => setForm(data)).catch(() => {});
    api.get("/shop/orders").then(({ data }) => setOrders(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/settings/shop", {
        shipping_cost: Number(form.shipping_cost) || 0,
        free_shipping_threshold: Number(form.free_shipping_threshold) || 0,
        enabled: !!form.enabled,
        welcome_enabled: !!form.welcome_enabled,
        welcome_code: (form.welcome_code || "BENVENUTO10").trim().toUpperCase(),
        welcome_pct: Number(form.welcome_pct) || 0,
        referral_enabled: form.referral_enabled !== false,
        referral_friend_pct: Number(form.referral_friend_pct) || 0,
        referral_referrer_pct: Number(form.referral_referrer_pct) || 0,
        referral_bonus_every: Number(form.referral_bonus_every) || 0,
        referral_bonus_pct: Number(form.referral_bonus_pct) || 0,
        bank_enabled: form.bank_enabled !== false,
        bank_iban: (form.bank_iban_raw ?? form.bank_iban ?? "").replace(/\s+/g, "").toUpperCase(),
        bank_holder: (form.bank_holder || "").trim(),
      });
      toast.success("Impostazioni negozio salvate");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markPaid = async (orderId) => {
    try {
      await api.post(`/shop/orders/${orderId}/mark-paid`);
      toast.success("Pagamento confermato · ricevuta inviata al cliente");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };

  const markShipped = async (orderId) => {
    const tracking = window.prompt("Codice di tracciamento (opzionale):", "") || "";
    try {
      await api.post(`/shop/orders/${orderId}/ship`, { tracking });
      toast.success("Ordine segnato come spedito · email inviata al cliente");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <ReviewsAdminPanel />
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Store className="w-5 h-5 text-[#0F4C81]" />
          <h3 className="font-serif text-xl font-semibold text-[#0B2A45]">Impostazioni negozio online</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
          <div><Label>Costo spedizione €</Label><Input type="number" step="0.01" data-testid="shop-shipping-input" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} className="mt-1" /></div>
          <div><Label>Soglia spedizione gratuita €</Label><Input type="number" step="0.01" data-testid="shop-threshold-input" value={form.free_shipping_threshold} onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })} className="mt-1" /></div>
          <div className="flex items-center gap-3">
            <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="shop-enabled-switch" />
            <Label>Negozio attivo</Label>
          </div>
          <div className="sm:col-span-2 border-t border-stone-100 pt-4 grid sm:grid-cols-3 gap-4" data-testid="referral-settings">
            <div><Label>Porta un amico: sconto amico %</Label><Input type="number" step="1" min="0" max="50" data-testid="shop-referral-friend-input" value={form.referral_friend_pct ?? 10} onChange={(e) => setForm({ ...form, referral_friend_pct: e.target.value })} className="mt-1" /></div>
            <div><Label>Premio a chi invita %</Label><Input type="number" step="1" min="0" max="50" data-testid="shop-referral-referrer-input" value={form.referral_referrer_pct ?? 10} onChange={(e) => setForm({ ...form, referral_referrer_pct: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.referral_enabled !== false} onCheckedChange={(v) => setForm({ ...form, referral_enabled: v })} data-testid="shop-referral-switch" />
              <Label>Programma amici attivo</Label>
            </div>
            <div><Label>Premio extra ogni N amici paganti</Label><Input type="number" step="1" min="0" max="100" data-testid="shop-referral-bonus-every-input" value={form.referral_bonus_every ?? 5} onChange={(e) => setForm({ ...form, referral_bonus_every: e.target.value })} className="mt-1" /></div>
            <div><Label>Sconto premio extra %</Label><Input type="number" step="1" min="0" max="50" data-testid="shop-referral-bonus-pct-input" value={form.referral_bonus_pct ?? 20} onChange={(e) => setForm({ ...form, referral_bonus_pct: e.target.value })} className="mt-1" /></div>
            <p className="text-xs text-stone-400 pt-7">0 = disattivato. Promemoria automatico dopo 7 giorni ai codici premio non usati.</p>
          </div>
          <div className="sm:col-span-2 border-t border-stone-100 pt-4 grid sm:grid-cols-3 gap-4">
            <div><Label>Codice sconto benvenuto</Label><Input data-testid="shop-welcome-code-input" value={form.welcome_code ?? "BENVENUTO10"} onChange={(e) => setForm({ ...form, welcome_code: e.target.value.toUpperCase() })} className="mt-1 font-mono" /></div>
            <div><Label>Sconto primo ordine %</Label><Input type="number" step="1" min="0" max="50" data-testid="shop-welcome-pct-input" value={form.welcome_pct ?? 10} onChange={(e) => setForm({ ...form, welcome_pct: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.welcome_enabled !== false} onCheckedChange={(v) => setForm({ ...form, welcome_enabled: v })} data-testid="shop-welcome-switch" />
              <Label>Sconto attivo</Label>
            </div>
          </div>
          <div className="sm:col-span-2 border-t border-stone-100 pt-4 grid sm:grid-cols-3 gap-4" data-testid="bank-settings">
            <div><Label>Intestatario bonifico</Label><Input data-testid="shop-bank-holder-input" value={form.bank_holder ?? ""} onChange={(e) => setForm({ ...form, bank_holder: e.target.value })} className="mt-1" placeholder="Botty srl" /></div>
            <div><Label>IBAN</Label><Input data-testid="shop-bank-iban-input" value={form.bank_iban_raw ?? ""} onChange={(e) => setForm({ ...form, bank_iban_raw: e.target.value.toUpperCase() })} className="mt-1 font-mono" placeholder="IT00X0000000000000000000000" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.bank_enabled !== false} onCheckedChange={(v) => setForm({ ...form, bank_enabled: v })} data-testid="shop-bank-switch" />
              <Label>Bonifico attivo</Label>
            </div>
            <p className="sm:col-span-3 text-xs text-stone-400 -mt-2">Gli ordini con bonifico ricevono un promemoria dopo 3 giorni e vengono annullati automaticamente dopo 7 se il pagamento non viene confermato.</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className={`px-3 py-1 rounded-full ${form.payment_ready ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="shop-payment-status">
              {form.payment_ready ? "Pagamento online attivo (Stripe)" : "Pagamento online non configurato"}
            </span>
          </div>
        </div>
        <Button onClick={save} data-testid="shop-save-btn" className="mt-6 bg-[#0F4C81] text-white rounded-full"><Save className="w-4 h-4 mr-2" />Salva impostazioni</Button>
        {!form.payment_ready && (
          <p className="text-xs text-stone-400 mt-3">Per attivare i pagamenti online configurare STRIPE_SECRET_KEY nel server.</p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <div className="p-4 border-b border-stone-100"><h3 className="font-serif text-lg font-semibold text-[#0B2A45]">Ordini online</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Rif.</TableHead><TableHead>Cliente</TableHead><TableHead>Email</TableHead><TableHead>Totale</TableHead><TableHead>Stato</TableHead><TableHead>Data</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-stone-400 text-center py-6">Nessun ordine online.</TableCell></TableRow>}
            {orders.map((o) => (
              <TableRow key={o.order_id} data-testid={`shop-order-${o.order_id}`}>
                <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                <TableCell className="font-medium">{(o.customer || {}).name}</TableCell>
                <TableCell className="text-sm text-stone-500">{(o.customer || {}).email}</TableCell>
                <TableCell className="font-mono">{eur(o.total)}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-stone-100"}`}>{o.status}</span></TableCell>
                <TableCell className="text-sm text-stone-500">{(o.created_at || "").slice(0, 10)}</TableCell>
                <TableCell>
                  {o.status === "AWAITING_TRANSFER" && (
                    <Button size="sm" onClick={() => markPaid(o.order_id)} data-testid={`mark-paid-${o.order_id}`} className="rounded-full h-8 text-xs bg-[#15803D] hover:bg-[#166534] text-white mr-2">
                      <BadgeEuro className="w-3.5 h-3.5 mr-1" />Segna come pagato
                    </Button>
                  )}
                  {["SHIPPED", "CANCELLED", "FAILED"].includes(o.status) ? (
                    o.status === "SHIPPED" ? <span className="text-xs text-blue-600 inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" />Spedito</span> : <span className="text-xs text-stone-400">—</span>
                  ) : o.status !== "AWAITING_TRANSFER" && (
                    <Button size="sm" variant="outline" onClick={() => markShipped(o.order_id)} data-testid={`ship-order-${o.order_id}`} className="rounded-full h-8 text-xs">
                      <Truck className="w-3.5 h-3.5 mr-1" />Segna spedito
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ReferralLeaderboard />
      <CleanupPanel />
    </div>
  );
}
